import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة المعايرة الكيميائية - حساب التركيز والحجم في عمليات التسحيح";
const DESCRIPTION =
  "حاسبة المعايرة الكيميائية المجانية - احسب تركيز أو حجم المحاليل في عمليات التسحيح (المعايرة) بدقة. تدعم معايرة حمض-قاعدة وحسابات المولارية. أداة دقيقة للمختبرات والطلاب.";
const KEYWORDS =
  "حاسبة المعايرة, حساب التركيز, حساب الحجم, معايرة حمض قاعدة, قانون المعايرة, المولارية, التسحيح الكيميائي, titration calculator, molarity calculator";
const OG_URL = "https://do-calculate.com/calculator/ar/titration/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة المعايرة الكيميائية",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "EducationalApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات الكيميائية",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة المعايرة",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "ما هو قانون المعايرة المستخدم؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "تستخدم الحاسبة قانون المعايرة الأساسي: (M1 × V1) / n1 = (M2 × V2) / n2، حيث M هو التركيز المولي، V هو الحجم، و n هو عدد المولات في المعادلة الموزونة.",
        },
      },
      {
        "@type": "Question",
        name: "لماذا نحتاج لعدد المولات (n) في الحساب؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "عدد المولات ضروري لموازنة المعادلة الكيميائية، خاصة عند معايرة أحماض وقواعد ثنائية أو ثلاثية البروتون لضمان دقة النتيجة.",
        },
      },
    ],
  },
];

function TitrationCalculator() {
  // States
  const [solveFor, setSolveFor] = useState("M2"); // Default calculate unknown concentration
  const [m1, setM1] = useState(""); // Concentration of Titrant
  const [v1, setV1] = useState(""); // Volume of Titrant
  const [n1, setN1] = useState("1"); // Coefficient of Titrant
  const [m2, setM2] = useState(""); // Concentration of Analyte
  const [v2, setV2] = useState(""); // Volume of Analyte
  const [n2, setN2] = useState("1"); // Coefficient of Analyte

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateTitration = () => {
    try {
      setError("");
      const M1 = parseFloat(m1);
      const V1 = parseFloat(v1);
      const N1 = parseFloat(n1);
      const M2 = parseFloat(m2);
      const V2 = parseFloat(v2);
      const N2 = parseFloat(n2);

      if (N1 <= 0 || N2 <= 0) {
        setError("يجب أن يكون عدد المولات (n) أكبر من صفر");
        setResult(null);
        return;
      }

      let finalValue = 0;
      let label = "";
      let unit = "";

      switch (solveFor) {
        case "M2":
          if (!M1 || !V1 || !V2) return;
          finalValue = (M1 * V1 * N2) / (V2 * N1);
          label = "تركيز المادة المجهولة (M2)";
          unit = "M (مول/لتر)";
          break;
        case "V2":
          if (!M1 || !V1 || !M2) return;
          finalValue = (M1 * V1 * N2) / (M2 * N1);
          label = "حجم المادة المجهولة (V2)";
          unit = "مل (mL)";
          break;
        case "M1":
          if (!M2 || !V2 || !V1) return;
          finalValue = (M2 * V2 * N1) / (V1 * N2);
          label = "تركيز المحلول القياسي (M1)";
          unit = "M (مول/لتر)";
          break;
        case "V1":
          if (!M2 || !V2 || !M1) return;
          finalValue = (M2 * V2 * N1) / (M1 * N2);
          label = "حجم المحلول القياسي (V1)";
          unit = "مل (mL)";
          break;
        default:
          break;
      }

      if (isNaN(finalValue) || !isFinite(finalValue)) {
        setResult(null);
      } else {
        setResult({
          value: finalValue,
          label,
          unit,
          formula: `(${M1 || 'M1'} × ${V1 || 'V1'} × ${N2 || 'n2'}) / (${N1 || 'n1'} × ${V2 || 'V2'})`
        });
      }
    } catch (err) {
      setError("خطأ في الحساب، يرجى التحقق من القيم المدخلة");
      setResult(null);
    }
  };

  useEffect(() => {
    calculateTitration();
  }, [solveFor, m1, v1, n1, m2, v2, n2]);

  const handleClear = () => {
    setM1("");
    setV1("");
    setN1("1");
    setM2("");
    setV2("");
    setN2("1");
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة المعايرة (التسحيح)</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة المعايرة</strong> هي أداة مخبرية دقيقة تساعد الكيميائيين والطلاب على <strong>حساب التركيز المولاري</strong> أو <strong>الحجم</strong> المطلوب في تفاعلات التعادل. تعتمد الحاسبة على قانون المعايرة المولي لضمان نتائج صحيحة عند التعامل مع الأحماض والقواعد بمختلف قوتها وتعدد بروتوناتها.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  إجراء حسابات المعايرة
                </h2>
              </div>

              <div className="row mb-3">
                <div className="col-sm-12">
                  <label >المتغير المطلوب حسابه</label>
                  <select
                    className="form-select"
                    value={solveFor}
                    onChange={(e) => {
                      setSolveFor(e.target.value);
                      handleClear();
                    }}
                  >
                    <option value="M2">تركيز المادة المجهولة (M2)</option>
                    <option value="V2">حجم المادة المجهولة (V2)</option>
                    <option value="M1">تركيز المحلول القياسي (M1)</option>
                    <option value="V1">حجم المحلول القياسي (V1)</option>
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-12"><h5 className="text-secondary border-bottom pb-2">المحلول القياسي (المعلوم)</h5></div>
                <div className="col-sm-12 col-md-4">
                  <label >التركيز M1 (مول/لتر)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={m1}
                    disabled={solveFor === "M1"}
                    onChange={(e) => setM1(e.target.value)}
                    placeholder="M1"
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label >الحجم V1 (مل)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={v1}
                    disabled={solveFor === "V1"}
                    onChange={(e) => setV1(e.target.value)}
                    placeholder="V1"
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label >عدد المولات n1</label>
                  <input
                    type="number"
                    className="form-control"
                    value={n1}
                    onChange={(e) => setN1(e.target.value)}
                    placeholder="n1"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12"><h5 className="text-secondary border-bottom pb-2">المحلول المجهول (المُحلل)</h5></div>
                <div className="col-sm-12 col-md-4">
                  <label >التركيز M2 (مول/لتر)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={m2}
                    disabled={solveFor === "M2"}
                    onChange={(e) => setM2(e.target.value)}
                    placeholder="M2"
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label >الحجم V2 (مل)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={v2}
                    disabled={solveFor === "V2"}
                    onChange={(e) => setV2(e.target.value)}
                    placeholder="V2"
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label >عدد المولات n2</label>
                  <input
                    type="number"
                    className="form-control"
                    value={n2}
                    onChange={(e) => setN2(e.target.value)}
                    placeholder="n2"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ padding: "10px 30px" }}>
                  امسح البيانات
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>النتيجة:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>المتغير</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>القيمة المستخرجة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>{result.label}</td>
                          <td style={{ textAlign: "center", fontSize: "20px", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.value.toFixed(4)} {result.unit}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هي عملية المعايرة الكيميائية؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  المعايرة هي تقنية تحليلية كيميائية مخبرية تُستخدم لتحديد تركيز محلول مجهول (يُسمى المُحلل) عن طريق تفاعله مع حجم معلوم من محلول ذو تركيز معروف بدقة (يُسمى المحلول القياسي أو المعاير).
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>المحلول القياسي:</strong> هو المحلول الذي نضعه عادة في السحاحة.</li>
                  <li><strong>نقطة التكافؤ:</strong> هي اللحظة التي تتساوى فيها كمية المادة القياسية المضافة مع كمية المادة المجهولة كيميائياً.</li>
                  <li><strong>الكاشف (الدليل):</strong> مادة يتغير لونها عند الوصول لنقطة نهاية المعايرة.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>قوانين حساب المعايرة</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  يعتمد الحساب بشكل أساسي على النسبة المولية من المعادلة الموزونة:
                </p>
                <div className="alert alert-info text-center" style={{ direction: "ltr" }}>
                  <strong>(M1 × V1) / n1 = (M2 × V2) / n2</strong>
                </div>
                <p>حيث:</p>
                <ul>
                  <li>M: المولارية (مول/لتر).</li>
                  <li>V: الحجم (يجب استخدام نفس الوحدات للطرفين).</li>
                  <li>n: عدد المولات في المعادلة المتوازنة.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية على حساب المعايرة</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px" }}>مثال 1: معايرة حمض HCl مع قاعدة NaOH</h3>
                <p>تفاعل: HCl + NaOH → NaCl + H2O (النسبة 1:1)</p>
                <p>إذا احتجنا 25 مل من NaOH بتركيز 0.1M لمعايرة 20 مل من HCl، فما تركيز الحمض؟</p>
                <div className="alert alert-success">
                  النتيجة: M2 = (0.1 × 25 × 1) / (20 × 1) = 0.125 M
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>مثال 2: حمض ثنائي البروتون</h3>
                <p>تفاعل: H2SO4 + 2NaOH → Na2SO4 + 2H2O</p>
                <p>هنا n حمض = 1، و n قاعدة = 2. يجب إدخال هذه القيم في خانة عدد المولات لضمان دقة الحساب.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح للاستخدام الصحيح</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "1.8" }}>
                  <li>تأكد من كتابة المعادلة الكيميائية الموزونة قبل البدء لتحديد قيم n1 و n2.</li>
                  <li>تأكد من قراءة حجم السحاحة بدقة عند مستوى تقعر السائل.</li>
                  <li>إذا كان الحجم بالمليلتر في طرف، يجب أن يكون بالمليلتر في الطرف الآخر.</li>
                  <li>استخدم كاشفاً مناسباً يتغير لونه عند الرقم الهيدروجيني المتوقع لنقطة التكافؤ.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>الأسئلة الشائعة حول المعايرة</strong>
              </div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>1. ما الفرق بين نقطة التكافؤ ونقطة النهاية؟</strong>
                  <p>نقطة التكافؤ هي نقطة نظرية تتساوى فيها المولات، بينما نقطة النهاية هي اللحظة التي يتغير فيها لون الكاشف فعلياً ونوقف عندها الإضافة.</p>
                </div>
                <div className="mb-3">
                  <strong>2. هل يمكن استخدام الحجم باللتر؟</strong>
                  <p>نعم، يمكنك استخدام اللتر أو المليلتر، بشرط توحيد الوحدة في طرفي المعادلة للحصول على نتيجة صحيحة.</p>
                </div>
                <div className="mb-3">
                  <strong>3. كيف أتعامل مع الأحماض القوية والقواعد الضعيفة؟</strong>
                  <p>الحسابات المولية تظل كما هي، ولكن اختيار الدليل الكيميائي هو الذي يختلف ليتناسب مع الوسط عند نقطة التعادل.</p>
                </div>
                <div className="mb-3">
                  <strong>4. ما هي المولارية؟</strong>
                  <p>المولارية هي مقياس لتركيز المذاب في المحلول، وتساوي عدد مولات المذاب مقسومة على حجم المحلول باللتر.</p>
                </div>
                <div className="mb-3">
                  <strong>5. لماذا تظهر النتائج بصيغة عشرية طويلة؟</strong>
                  <p>الدقة مهمة في الكيمياء؛ الحاسبة تعرض 4 أرقام عشرية لضمان دقة التحليل المخبري وتجنب أخطاء التقريب الكبيرة.</p>
                </div>
                <div className="mb-3">
                  <strong>6. كيف أحسب عدد المولات (n)؟</strong>
                  <p>تؤخذ هذه القيم مباشرة من المعاملات الموجودة يسار الصيغة الكيميائية في المعادلة الكيميائية الموزونة.</p>
                </div>
                <div className="mb-3">
                  <strong>7. ماذا أفعل إذا كان التفاعل ليس حمض وقاعدة؟</strong>
                  <p>قانون (MV/n) ينطبق على أي تفاعل معايرة (مثل معايرة الأكسدة والاختزال) طالما أنك تملك المعادلة الموزونة.</p>
                </div>
                <div className="mb-3">
                  <strong>8. هل الحاسبة تدعم درجات الحرارة؟</strong>
                  <p>الحسابات تعتمد على الحجم والتركيز القياسي؛ في الظروف المخبرية العادية نعتبر الحجم ثابتاً ولكن يفضل إجراء التجارب في درجة حرارة الغرفة.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TitrationCalculator;